import { NavMain } from '@/components/navigation/NavMain';
import { NavGroupedMenu } from '@/components/navigation/NavGroupedMenu';
import { NavUser } from '@/components/navigation/NavUser';
import { RegistrySelector } from '@/components/registry/RegistrySelector';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { Permissions } from '@/lib/permissions';
import { type NavItem, type NavGroupItem } from '@/types';
import { Link, useLocation, useParams } from 'react-router';
import {
    AppWindow,
    ArrowLeft,
    LayoutDashboard,
    GitBranch,
    Bot,
    Brain,
    MessageSquare,
    Plug,
    Server,
    Wrench,
    Sparkles,
    UserCog,
    Users,
    Shield,
    Scale,
    FileJson,
    Bell,
    Tags,
    Link2,
    Box,
    LogOut,
    Network,
    Settings,
    Key,
    CreditCard,
    Store,
    Package,
    Receipt,
    Database,
    DollarSign,
    Timer,
    Puzzle,
    Cable,
    Building2,
    Handshake,
    Eye,
    FolderOpen,
} from 'lucide-react';

const platformNavGroups: NavGroupItem[] = [
    { title: 'Dashboard', icon: LayoutDashboard, href: '/' },
    { title: 'Chat', icon: MessageSquare, href: '/chat' },
    { title: 'Applications', icon: AppWindow, href: '/applications' },
    {
        title: 'Infrastructure',
        icon: Server,
        items: [
            { title: 'Workers', href: '/workers', icon: Server },
            { title: 'Scheduler', href: '/scheduler/triggers', icon: Timer },
            { title: 'Registries', href: '/registries', icon: Network },
            { title: 'Worker Credentials', href: '/settings/workers', icon: Server },
        ],
    },
    {
        title: 'Security & Access',
        icon: Key,
        items: [
            { title: 'OAuth Clients', href: '/settings/oauth-clients', icon: Key },
            { title: 'Federation Peers', href: '/settings/federation-peers', icon: Users },
        ],
    },
    {
        title: 'Billing',
        icon: CreditCard,
        items: [
            { title: 'Billing', href: '/settings/billing', icon: CreditCard },
            { title: 'Subscriptions', href: '/settings/subscriptions', icon: Bell },
            { title: 'Usage & Charges', href: '/settings/ledger', icon: Receipt },
            { title: 'Products', href: '/settings/marketplace', icon: Package },
        ],
    },
    {
        title: 'Integrations',
        icon: Puzzle,
        items: [
            { title: 'Integrations', href: '/integrations', icon: Puzzle },
            { title: 'Connections', href: '/connections', icon: Cable },
        ],
    },
    {
        title: 'White-Label',
        icon: Building2,
        items: [
            { title: 'Accounts', href: '/accounts', icon: Building2 },
            { title: 'Partners', href: '/partners', icon: Handshake },
        ],
    },
    { title: 'Settings', icon: Settings, href: '/settings' },
];

const marketplaceNavItems: NavItem[] = [
    { title: 'Browse', href: '/marketplace', icon: Store },
    { title: 'Platform Billing', href: '/admin/billing', icon: DollarSign, requiredPermission: Permissions.PLATFORM_BILLING_READ },
];

const appNavGroups = (appKey: string): NavGroupItem[] => [
    { title: 'Overview', icon: LayoutDashboard, href: `/applications/${appKey}` },
    { title: 'Visibility', icon: Eye, href: `/applications/${appKey}/visibility` },
    {
        title: 'AI & Orchestration',
        icon: Bot,
        items: [
            { title: 'Workflows', href: `/applications/${appKey}/capabilities?type=workflow`, icon: GitBranch },
            { title: 'Agents', href: `/applications/${appKey}/capabilities?type=agent`, icon: Bot },
            { title: 'Models', href: `/applications/${appKey}/capabilities?type=model`, icon: Brain },
            { title: 'Prompts', href: `/applications/${appKey}/capabilities?type=prompt`, icon: MessageSquare },
            { title: 'Skills', href: `/applications/${appKey}/capabilities?type=skill`, icon: Sparkles },
            { title: 'Tools', href: `/applications/${appKey}/capabilities?type=tool`, icon: Wrench },
            { title: 'Memory', href: `/applications/${appKey}/capabilities?type=memory`, icon: Database },
        ],
    },
    {
        title: 'Data & Schema',
        icon: Database,
        items: [
            { title: 'Schemas', href: `/applications/${appKey}/capabilities?type=schema`, icon: FileJson },
            { title: 'Domain Types', href: `/applications/${appKey}/capabilities?type=domain-type`, icon: Tags },
        ],
    },
    {
        title: 'Governance',
        icon: Shield,
        items: [
            { title: 'Roles', href: `/applications/${appKey}/capabilities?type=role`, icon: UserCog },
            { title: 'Permissions', href: `/applications/${appKey}/capabilities?type=permission`, icon: Key },
            { title: 'Policies', href: `/applications/${appKey}/capabilities?type=policy`, icon: Shield },
            { title: 'Decisions', href: `/applications/${appKey}/capabilities?type=decision`, icon: Scale },
        ],
    },
    {
        title: 'Connectivity',
        icon: Plug,
        items: [
            { title: 'Integrations', href: `/applications/${appKey}/capabilities?type=integration`, icon: Puzzle },
            { title: 'Connections', href: `/applications/${appKey}/capabilities?type=connection`, icon: Cable },
            { title: 'Providers', href: `/applications/${appKey}/capabilities?type=provider`, icon: Plug },
            { title: 'Bindings', href: `/applications/${appKey}/capabilities?type=binding`, icon: Link2 },
            { title: 'Subscriptions', href: `/applications/${appKey}/capabilities?type=subscription`, icon: Bell },
            { title: 'API Access', href: `/applications/${appKey}/settings/api-access`, icon: Key },
        ],
    },
    {
        title: 'Infrastructure',
        icon: Server,
        items: [
            { title: 'Workers', href: `/applications/${appKey}/capabilities?type=worker`, icon: Server },
            { title: 'Sandboxes', href: `/applications/${appKey}/capabilities?type=sandbox`, icon: Box },
            { title: 'Workspaces', href: `/applications/${appKey}/capabilities?type=workspace`, icon: FolderOpen },
        ],
    },
    {
        title: 'Billing',
        icon: CreditCard,
        items: [
            { title: 'Billing', href: `/applications/${appKey}/billing`, icon: CreditCard },
            { title: 'Subscriptions', href: `/applications/${appKey}/billing/subscriptions`, icon: Bell },
            { title: 'Usage & Charges', href: `/applications/${appKey}/billing/ledger`, icon: Receipt },
            { title: 'Products', href: `/applications/${appKey}/billing/products`, icon: Package },
        ],
    },
    { title: 'Settings', icon: Settings, href: `/applications/${appKey}/settings` },
];

interface AppContext {
    appKey: string;
    name: string;
}

function getAppContextFromUrl(url: string): AppContext | null {
    const match = url.match(/\/applications\/([a-zA-Z0-9._-]+)(?:\/|$)/);
    if (match) {
        return { appKey: match[1], name: '' };
    }
    return null;
}

export function AdminSidebar() {
    const { pathname } = useLocation();
    const params = useParams();
    const { logout } = useAuth();
    const { hasPermission, isPlatformAdmin } = usePermissions();

    const urlAppContext = getAppContextFromUrl(pathname);
    const isInApp = urlAppContext !== null;
    const appKey = params.appKey || urlAppContext?.appKey || '';
    const currentApp: AppContext | null = isInApp
        ? { appKey, name: `App ${appKey}` }
        : null;

    const filteredMarketplaceNavItems = marketplaceNavItems.filter(item => {
        if (!item.requiredPermission) return true;
        return isPlatformAdmin || hasPermission(item.requiredPermission);
    });

    return (
        <Sidebar collapsible="icon" variant="sidebar">
            <SidebarHeader className="h-16 flex items-center px-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                <Link
                    to="/"
                    className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:justify-center"
                >
                    <img
                        src="/mz_logo_blue_gold_clear.png"
                        alt=""
                        className="h-[2.4rem] w-auto max-w-[144px] object-contain object-left shrink-0"
                    />
                    <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                        <p className="font-semibold truncate text-[calc(0.875rem*1.1*1.1)]">
                            More0
                        </p>
                        <p className="text-sidebar-foreground/80 text-[calc(0.75rem*1.2)]">
                            Platform Management
                        </p>
                    </div>
                </Link>
            </SidebarHeader>

            <SidebarContent>
                {isInApp && currentApp ? (
                    <>
                        <SidebarGroup className="px-2 py-1">
                            <SidebarMenu className="gap-0.5">
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        size="sm"
                                        tooltip="Back to Applications"
                                    >
                                        <Link
                                            to="/applications"
                                            className="text-sidebar-foreground hover:text-sidebar-accent-foreground"
                                        >
                                            <ArrowLeft size={16} />
                                            <span>Back to Applications</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroup>

                        <NavGroupedMenu groups={appNavGroups(currentApp.appKey)} />
                    </>
                ) : (
                    <>
                        <SidebarGroup className="px-2 py-1">
                            <SidebarGroupLabel className="h-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Active Registry
                            </SidebarGroupLabel>
                            <RegistrySelector />
                        </SidebarGroup>

                        <NavGroupedMenu groups={platformNavGroups} />
                        <SidebarGroup className="px-2 py-0">
                            <SidebarGroupLabel className="h-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Marketplace
                            </SidebarGroupLabel>
                        </SidebarGroup>
                        <NavMain items={filteredMarketplaceNavItems} />
                    </>
                )}
            </SidebarContent>

            <SidebarFooter>
                <NavUser />
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            asChild
                            tooltip="Log out"
                            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        >
                            <button
                                onClick={logout}
                                className="w-full justify-start"
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Log out</span>
                            </button>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}

export default AdminSidebar;
